import { useState, useEffect } from 'react'
import { Settings, Zap, Lock, Database } from 'lucide-react'
import { supabase } from '../services/supabase'
import { ApiUsageDashboard } from '../components/Settings/ApiUsageDashboard'
import { SupabaseUsageDashboard } from '../components/Settings/SupabaseUsageDashboard'
import { useAppSettings } from '../hooks/useAppSettings'

export function SettingsPage({ isDarkMode = false }) {
  const [activeTab, setActiveTab] = useState('api-usage')
  const [userEmail, setUserEmail] = useState('')
  const [importEnabled, setImportEnabled] = useState(false)
  const { isEnabled, updateSetting } = useAppSettings()

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email) setUserEmail(session.user.email)
    })
    const saved = localStorage.getItem('sfp_enable_transaction_import')
    setImportEnabled(saved === 'true')
  }, [])

  const isAdmin = userEmail === 'test@sfpwealth.co.za'

  const handleToggle = () => {
    const newValue = !importEnabled
    setImportEnabled(newValue)
    localStorage.setItem('sfp_enable_transaction_import', newValue ? 'true' : 'false')
  }

  return (
    <div className={`min-h-screen p-8 transition-colors duration-300 ${isDarkMode ? 'bg-slate-900' : 'bg-sfp-white'}`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold flex items-center gap-3 ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>
            <Settings className="w-8 h-8 text-sfp-orange" />
            Settings
          </h1>
          <p className={`mt-2 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Manage your application settings and track API usage</p>
        </div>

        {/* Tab Navigation */}
        <div className={`flex gap-2 mb-8 border-b transition-colors duration-300 ${isDarkMode ? 'border-slate-700' : 'border-sfp-border'}`}>
          <button
            onClick={() => setActiveTab('api-usage')}
            className={`px-6 py-3 font-bold text-sm uppercase tracking-wide flex items-center gap-2 transition-colors ${
              activeTab === 'api-usage'
                ? 'text-sfp-orange border-b-2 border-sfp-orange'
                : isDarkMode ? 'text-slate-500 hover:text-slate-300' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Zap className="w-4 h-4" />
            API Usage
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('supabase-usage')}
              className={`px-6 py-3 font-bold text-sm uppercase tracking-wide flex items-center gap-2 transition-colors ${
                activeTab === 'supabase-usage'
                  ? 'text-sfp-orange border-b-2 border-sfp-orange'
                  : isDarkMode ? 'text-slate-500 hover:text-slate-300' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Database className="w-4 h-4" />
              Supabase Usage
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('admin')}
              className={`px-6 py-3 font-bold text-sm uppercase tracking-wide flex items-center gap-2 transition-colors ${
                activeTab === 'admin'
                  ? 'text-sfp-orange border-b-2 border-sfp-orange'
                  : isDarkMode ? 'text-slate-500 hover:text-slate-300' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Lock className="w-4 h-4" />
              Admin
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setActiveTab('debug')}
              className={`px-6 py-3 font-bold text-sm uppercase tracking-wide flex items-center gap-2 transition-colors ${
                activeTab === 'debug'
                  ? 'text-sfp-orange border-b-2 border-sfp-orange'
                  : isDarkMode ? 'text-slate-500 hover:text-slate-300' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Zap className="w-4 h-4" />
              Debug
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'api-usage' && (
          <div className={isDarkMode ? 'bg-slate-900' : 'bg-sfp-white'}>
            <ApiUsageDashboard />
          </div>
        )}

        {activeTab === 'supabase-usage' && isAdmin && (
          <div className={isDarkMode ? 'bg-slate-900' : 'bg-sfp-white'}>
            <SupabaseUsageDashboard userEmail={userEmail} />
          </div>
        )}

        {activeTab === 'admin' && isAdmin && (
          <div className={`p-6 rounded border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-sfp-white border-sfp-border'}`}>
            <h2 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>Feature Controls</h2>

            <div className="flex flex-col gap-4">
              {/* Policy Transaction Import */}
              <FeatureToggle
                title="Policy Transaction Import"
                description="Enable Excel import for commission transactions"
                enabled={importEnabled}
                onToggle={handleToggle}
                isDarkMode={isDarkMode}
              />

              {/* Tasks Tab */}
              <FeatureToggle
                title="Tasks Tab"
                description="Show the Tasks tab in the sidebar for all users. When off, only admin can see it."
                enabled={isEnabled('feature_tasks')}
                onToggle={() => updateSetting('feature_tasks', isEnabled('feature_tasks') ? 'false' : 'true')}
                isDarkMode={isDarkMode}
              />

              {/* Claims Tasks Tab */}
              <FeatureToggle
                title="Claims Tasks Tab"
                description="Show the Claims Tasks tab in the sidebar for all users. When off, only admin can see it."
                enabled={isEnabled('feature_claims_tasks')}
                onToggle={() => updateSetting('feature_claims_tasks', isEnabled('feature_claims_tasks') ? 'false' : 'true')}
                isDarkMode={isDarkMode}
              />
            </div>
          </div>
        )}

        {activeTab === 'debug' && isAdmin && (
          <div className={`p-6 rounded border transition-colors duration-300 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-sfp-white border-sfp-border'}`}>
            <h2 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>Error Tracking Test</h2>
            <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              Click the button below to test Sentry error tracking. The error will appear in your Sentry dashboard within seconds.
            </p>
            <button
              onClick={() => {
                throw new Error('This is a test error from the Debug panel')
              }}
              className="px-6 py-3 rounded-xl font-bold text-white transition-all duration-200"
              style={{
                backgroundColor: '#dc2626',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#b91c1c' }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#dc2626' }}
            >
              🔴 Trigger Test Error
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function FeatureToggle({ title, description, enabled, onToggle, isDarkMode = false }) {
  return (
    <div className={`border rounded-lg p-6 transition-colors duration-300 ${isDarkMode ? 'border-slate-700 bg-slate-700' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`font-bold ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>{title}</h3>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>{description}</p>
        </div>
        <button
          onClick={onToggle}
          className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
            enabled ? 'bg-green-500' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-7' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      <p className={`text-xs mt-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{enabled ? '✓ ENABLED' : '✗ DISABLED'}</p>
    </div>
  )
}
